import type { components } from '@/lib/api-types-administration';

/**
 * The place an administrator is working on (ADR-018): the platform, a tenant or an organization.
 *
 * It lives in the area's own address (FR-AD-1, decision 60) — `/admin/platform`,
 * `/admin/tenants/:id`, `/admin/organizations/:id` — and never in the shell's active context,
 * which stays the organization every MODULE works in. Keeping it in the URL is what makes a deep
 * link, a reload and the back button land on the same place, with nothing to restore.
 */
export type Place =
  | { readonly kind: 'platform' }
  | { readonly kind: 'tenant'; readonly id: string }
  | { readonly kind: 'organization'; readonly id: string };

export type AdministrationScope = components['schemas']['AdministrationScope'];
export type PlaceCan = AdministrationScope['can'];
export type PlaceRef = components['schemas']['PlaceRef'];

/**
 * What the client knows about one place: its name, its tenant, and the effective `can` there. It
 * comes from the place read (`GET /v1/administration/{placeKind}/{placeId}`, added with the
 * backend's place mode) or, for a granted place, from its own entry in `/scopes`.
 */
export type PlaceDetail = components['schemas']['AdministeredPlace'];

export function placeKey(place: Place): string {
  return place.kind === 'platform' ? 'platform' : `${place.kind}:${place.id}`;
}

/** Every section the area can show, in the order the rail lists them (FR-AD-2). */
export const SECTION_IDS = [
  'overview',
  'tenants',
  'organizations',
  'people',
  'roles',
  'invitations',
  'modules',
  'role-catalog',
] as const;
export type SectionId = (typeof SECTION_IDS)[number];

const BASE = '/admin';

export function placeBasePath(place: Place): string {
  switch (place.kind) {
    case 'platform':
      return `${BASE}/platform`;
    case 'tenant':
      return `${BASE}/tenants/${encodeURIComponent(place.id)}`;
    case 'organization':
      return `${BASE}/organizations/${encodeURIComponent(place.id)}`;
  }
}

/** The address of one section at one place: the section is the last segment, named as its id. */
export function sectionPath(place: Place, section: SectionId): string {
  return `${placeBasePath(place)}/${section}`;
}

/**
 * The `{placeKind}/{placeId}` pair of the place-scoped routes. The platform has none: its own
 * routes are spelled out (`/v1/administration/platform/...`) and are evaluated in the caller's
 * active organization instead.
 */
export function apiPlace(
  place: Place,
): { placeKind: 'tenants' | 'organizations'; placeId: string } | null {
  switch (place.kind) {
    case 'platform':
      return null;
    case 'tenant':
      return { placeKind: 'tenants', placeId: place.id };
    case 'organization':
      return { placeKind: 'organizations', placeId: place.id };
  }
}

export function placeFromRef(ref: PlaceRef): Place | null {
  if (ref.kind === 'platform') {
    return { kind: 'platform' };
  }
  if (ref.id === null || ref.id === undefined || ref.id === '') {
    return null;
  }
  return { kind: ref.kind, id: ref.id };
}

export function samePlace(a: Place, b: Place): boolean {
  if (a.kind !== b.kind) {
    return false;
  }
  return a.kind === 'platform' || (b.kind !== 'platform' && a.id === b.id);
}
